import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Spinner, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory, faSearch, faSyncAlt, faEye, faChevronLeft, faChevronRight,
  faCheckCircle, faExclamationTriangle, faTimes, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { InscriptionContext } from '../layout/CunstomInscription';

// ============================================================
// CACHE DEXIE (24h) avec possibilité de récupérer le cache périmé
// ============================================================
const db = new Dexie('HistoriqueCacheDB');
db.version(1).stores({ historique: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedHistorique(key, allowExpired = false) {
  try {
    const entry = await db.historique.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedHistorique(key, data) {
  try {
    await db.historique.put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}

async function getStaleHistorique(key) {
  return getCachedHistorique(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES (Map globale)
// ============================================================
const pendingFetches = new Map();

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

const API_URL = `${API_CONFIG2.apiUrl}inscription/api_historique.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const HistoriqueInscriptions = () => {
  const context = useContext(InscriptionContext) || {};
  const { refreshKey, setSource } = context;

  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState('date_action_desc');
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [showModal, setShowModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // FONCTION DE FETCH AVEC DÉDOUBLONNAGE + ANNULATION
  // ============================================================
  const fetchHistorique = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_limit_${pagination.limit}_search_${search}_sort_${sort}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setHistorique(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
          setError('');
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedHistorique(cacheKey);
          if (cached && isMounted.current) {
            setHistorique(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&sort=${sort}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleHistorique(cacheKey);
          if (stale && isMounted.current) {
            setHistorique(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchHistorique(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleHistorique(cacheKey);
          if (stale && isMounted.current) {
            setHistorique(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setHistorique(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedHistorique(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
            setError('');
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleHistorique(cacheKey);
        if (stale && isMounted.current) {
          setHistorique(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          setError(err.message);
          showToast(err.message, 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, sort, showToast, setSource]);

  // ============================================================
  // GESTION DE LA RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchHistorique(1, val, false);
    }, 400);
  };

  // ============================================================
  // CHANGEMENT DE PAGE
  // ============================================================
  const changePage = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchHistorique(newPage, searchTerm, false);
  };

  // ============================================================
  // CHANGEMENT DE TRI
  // ============================================================
  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSort(newSort);
    // On recharge avec le nouveau tri (page 1)
    fetchHistorique(1, searchTerm, false);
  };

  // ============================================================
  // OUVERTURE / FERMETURE DU MODAL
  // ============================================================
  const openModal = (entry) => {
    setSelectedEntry(entry);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEntry(null);
  };

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchHistorique(1, '', false);
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchHistorique(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchHistorique, pagination.currentPage, searchTerm]);

  // ============================================================
  // RENDU (avec styles responsifs améliorés)
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – HISTORIQUE INSCRIPTIONS (amélioré)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .historique-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 28px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .historique-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
        }
        .historique-header h1 {
          font-size: clamp(1.6rem, 4vw, 2.2rem);
          font-weight: 700;
          margin: 0;
          color: var(--text-primary);
        }
        .historique-header h1 svg {
          color: var(--accent-blue);
        }
        .historique-header p {
          font-size: clamp(0.75rem, 1.2vw, 0.95rem);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.secondary {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .monetra-btn.secondary:hover:not(:disabled) {
          background: rgba(255,255,255,0.1);
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .glass-table td {
          padding: 12px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tbody tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .badge-action {
          display: inline-block;
          padding: 2px 12px;
          border-radius: 16px;
          font-size: clamp(0.65rem, 0.8vw, 0.75rem);
          font-weight: 600;
        }
        .badge-action.create {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }
        .badge-action.update {
          background: rgba(96, 165, 250, 0.15);
          color: #60a5fa;
        }
        .badge-action.delete {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
        }

        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.6);
          animation: slideIn 0.3s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        .toast-item.info { border-left: 4px solid #60a5fa; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-primary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
        }
        .btn-close-custom:hover { opacity: 1; }

        .modal-custom .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .modal-custom .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .modal-custom .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .modal-custom .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .json-view {
          background: rgba(0,0,0,0.3);
          border-radius: 12px;
          padding: 16px;
          max-height: 300px;
          overflow: auto;
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          white-space: pre-wrap;
          word-break: break-all;
          border: 1px solid rgba(255,255,255,0.04);
          color: var(--text-secondary);
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          padding-top: 16px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-buttons {
          display: flex;
          gap: 8px;
        }

        /* ============================================================
           RESPONSIVE
           ============================================================ */
        @media (max-width: 768px) {
          .historique-container { padding: 12px; }
          .historique-header { flex-direction: column; align-items: stretch; }
          .historique-header .d-flex { flex-wrap: wrap; justify-content: center; }
          .search-wrapper { min-width: unset; width: 100%; }
          .glass-card { padding: 1rem; }
          .glass-table th, .glass-table td { padding: 8px 6px; font-size: 0.7rem; }
          .monetra-btn { font-size: 0.75rem; padding: 6px 12px; min-height: 38px; }
          .pagination-controls { flex-direction: column; align-items: center; }
        }
        @media (max-width: 480px) {
          .glass-table th, .glass-table td { font-size: 0.6rem; padding: 4px 4px; }
          .monetra-btn { font-size: 0.7rem; padding: 4px 10px; min-height: 34px; }
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-warning { color: #f59e0b; }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-0 { padding: 0; }
        .px-3 { padding-left: 1rem; padding-right: 1rem; }
        .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .mt-3 { margin-top: 1rem; }
        .bg-transparent { background: transparent; }
        .border-secondary { border-color: rgba(255,255,255,0.08); }
        .form-select { appearance: none; background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e"); background-repeat: no-repeat; background-position: right 12px center; background-size: 16px; }
      `}</style>

      <div className="historique-container">
        <div className="historique-header">
          <div>
            <h1><FontAwesomeIcon icon={faHistory} className="me-2" />Historique des inscriptions</h1>
            <p className="text-secondary m-0">Consultez toutes les modifications apportées aux inscriptions</p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn primary" onClick={() => fetchHistorique(pagination.currentPage, searchTerm, false)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn secondary" onClick={() => fetchHistorique(pagination.currentPage, searchTerm, true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Forcer
            </button>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="glass-card mb-4">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="search-wrapper" style={{ flex: 1, minWidth: '200px' }}>
              <FontAwesomeIcon icon={faSearch} className="text-secondary" />
              <input
                type="text"
                placeholder="Rechercher (action, ID inscription, ID user...)"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div>
              <select
                className="form-select bg-transparent text-light border-secondary"
                value={sort}
                onChange={handleSortChange}
                style={{ borderRadius: '30px', padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', outline: 'none', minHeight: '44px' }}
                disabled={loading || refreshing}
              >
                <option value="date_action_desc" style={{ background: '#0b1428' }}>Date récente</option>
                <option value="date_action_asc" style={{ background: '#0b1428' }}>Date ancienne</option>
                <option value="id_desc" style={{ background: '#0b1428' }}>ID décroissant</option>
                <option value="id_asc" style={{ background: '#0b1428' }}>ID croissant</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Inscription ID</th>
                  <th>Action</th>
                  <th>Utilisateur</th>
                  <th>Date</th>
                  <th>Détails</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="6" className="text-center py-5"><Spinner variant="primary" /></td></tr>
                ) : historique.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-5 text-secondary">Aucun historique trouvé.</td></tr>
                ) : (
                  historique.map(item => (
                    <tr key={item.id}>
                      <td>#{item.id}</td>
                      <td>{item.inscription_id}</td>
                      <td>
                        <span className={`badge-action ${item.action.toLowerCase()}`}>
                          {item.action}
                        </span>
                      </td>
                      <td>{item.user_id || '—'}</td>
                      <td>{new Date(item.date_action).toLocaleString()}</td>
                      <td>
                        <button className="monetra-btn" onClick={() => openModal(item)} disabled={loading || refreshing}>
                          <FontAwesomeIcon icon={faEye} /> Voir
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-controls px-3 py-2">
            <span className="text-secondary small">
              {pagination.totalRecords} enregistrements · Page {pagination.currentPage} / {pagination.totalPages}
            </span>
            <div className="pagination-buttons">
              <button
                className="monetra-btn"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => changePage(pagination.currentPage - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className="monetra-btn"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => changePage(pagination.currentPage + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal détails */}
      <Modal show={showModal} onHide={closeModal} className="modal-custom" size="lg">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Détails de l'historique #{selectedEntry?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntry && (
            <div>
              <p><strong>Inscription ID :</strong> {selectedEntry.inscription_id}</p>
              <p><strong>Action :</strong> <span className={`badge-action ${selectedEntry.action.toLowerCase()}`}>{selectedEntry.action}</span></p>
              <p><strong>Utilisateur :</strong> {selectedEntry.user_id || 'Non spécifié'}</p>
              <p><strong>Date :</strong> {new Date(selectedEntry.date_action).toLocaleString()}</p>
              <hr />
              <h6>Anciennes données</h6>
              <div className="json-view">
                {selectedEntry.old_data ? (
                  <pre className="text-secondary">{JSON.stringify(selectedEntry.old_data, null, 2)}</pre>
                ) : (
                  <span className="text-secondary">Aucune donnée</span>
                )}
              </div>
              <h6 className="mt-3">Nouvelles données</h6>
              <div className="json-view text-secondary">
                {selectedEntry.new_data ? (
                  <pre className="text-secondary">{JSON.stringify(selectedEntry.new_data, null, 2)}</pre>
                ) : (
                  <span className="text-secondary">Aucune donnée</span>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} className="monetra-btn secondary">Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast notifications */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : toast.variant === 'warning' ? faExclamationTriangle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : toast.variant === 'warning' ? 'text-warning' : 'text-danger'}
            />
            <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoriqueInscriptions;